/**
 * Hero Banner Swiper Initialization
 * Handles Swiper.js setup and additional functionality
 */

class HeroBanner {
    constructor(element) {
      this.element = element;
      this.swiper = null;
      this.init();
    }
  
    init() {
      if (!this.element) return;
      
      // Wait for Swiper to be available
      if (typeof Swiper === 'undefined') {
        console.warn('Swiper.js not loaded. Hero banner will not function properly.');
        return;
      }
  
      this.initializeSwiper();
      this.setupEventListeners();
    }
  
    initializeSwiper() {
      const swiperElement = this.element.querySelector('.hero-swiper');
      if (!swiperElement) return;
  
      try {
        const config = JSON.parse(swiperElement.dataset.swiperConfig || '{}');
        
        // Default configuration
        const defaultConfig = {
          autoplay: false,
          loop: false,
          speed: 500,
          effect: 'slide',
          pagination: {
            el: '.swiper-pagination',
            clickable: true
          },
          navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev'
          },
          on: {
            init: () => this.onSwiperInit(),
            slideChange: () => this.onSlideChange()
          }
        };
  
        // Merge configurations
        const finalConfig = { ...defaultConfig, ...config };
        
        this.swiper = new Swiper(swiperElement, finalConfig);
      } catch (error) {
        console.error('Error initializing Swiper:', error);
      }
    }
  
    setupEventListeners() {
      // Pause autoplay on hover
      this.element.addEventListener('mouseenter', () => {
        if (this.swiper && this.swiper.autoplay) {
          this.swiper.autoplay.pause();
        }
      });
  
      this.element.addEventListener('mouseleave', () => {
        if (this.swiper && this.swiper.autoplay) {
          this.swiper.autoplay.resume();
        }
      });
  
      // Pause autoplay when video is playing
      const videos = this.element.querySelectorAll('.wanderlust-hero-slide__video');
      videos.forEach(video => {
        video.addEventListener('play', () => {
          if (this.swiper && this.swiper.autoplay) {
            this.swiper.autoplay.pause();
          }
        });
  
        video.addEventListener('pause', () => {
          if (this.swiper && this.swiper.autoplay) {
            this.swiper.autoplay.resume();
          }
        });
      });
  
      // Handle keyboard navigation
      this.element.addEventListener('keydown', (e) => {
        if (!this.swiper) return;
  
        switch(e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            this.swiper.slidePrev();
            break;
          case 'ArrowRight':
            e.preventDefault();
            this.swiper.slideNext();
            break;
          case ' ':
            e.preventDefault();
            if (this.swiper.autoplay) {
              if (this.swiper.autoplay.running) {
                this.swiper.autoplay.pause();
              } else {
                this.swiper.autoplay.resume();
              }
            }
            break;
        }
      });
  
      // Make the element focusable for keyboard navigation
      this.element.setAttribute('tabindex', '0');
    }
  
    onSwiperInit() {
      // Add loaded class for CSS animations
      this.element.classList.add('hero-banner--loaded');
      
      // Set up video autoplay for first slide
      this.setupVideoAutoplay();
    }
  
    onSlideChange() {
      // Handle video playback when slide changes
      this.handleVideoPlayback();
    }
  
    setupVideoAutoplay() {
      const firstSlide = this.element.querySelector('.swiper-slide-active .wanderlust-hero-slide__video');
      if (firstSlide) {
        firstSlide.play().catch(e => {
          console.log('Video autoplay prevented:', e);
        });
      }
    }
  
    handleVideoPlayback() {
      const videos = this.element.querySelectorAll('.wanderlust-hero-slide__video');
      const activeSlide = this.element.querySelector('.swiper-slide-active');
      
      videos.forEach(video => {
        if (activeSlide && activeSlide.contains(video)) {
          // Play video in active slide
          video.play().catch(e => {
            console.log('Video autoplay prevented:', e);
          });
        } else {
          // Pause videos in inactive slides
          video.pause();
        }
      });
    }
  
    // Public methods
    goToSlide(index) {
      if (this.swiper) {
        this.swiper.slideTo(index);
      }
    }
  
    nextSlide() {
      if (this.swiper) {
        this.swiper.slideNext();
      }
    }
  
    prevSlide() {
      if (this.swiper) {
        this.swiper.slidePrev();
      }
    }
  
    pauseAutoplay() {
      if (this.swiper && this.swiper.autoplay) {
        this.swiper.autoplay.pause();
      }
    }
  
    resumeAutoplay() {
      if (this.swiper && this.swiper.autoplay) {
        this.swiper.autoplay.resume();
      }
    }
  
    destroy() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }
  }
  
  // Initialize all hero banners on page load (only if not already initialized inline)
  document.addEventListener('DOMContentLoaded', function() {
    const heroBanners = document.querySelectorAll('.wanderlust-hero-banner:not([data-inline-initialized])');
    heroBanners.forEach(element => {
      new HeroBanner(element);
    });
  });
  
  // Re-initialize on theme editor changes
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      if (event.target.querySelector('.wanderlust-hero-banner')) {
        const heroBanner = event.target.querySelector('.wanderlust-hero-banner');
        new HeroBanner(heroBanner);
      }
    });
  
    document.addEventListener('shopify:section:unload', function(event) {
      const heroBanner = event.target.querySelector('.wanderlust-hero-banner');
      if (heroBanner && heroBanner.heroBannerInstance) {
        heroBanner.heroBannerInstance.destroy();
      }
    });
  }
  
  // Export for global access
  window.HeroBanner = HeroBanner;
  